/**
 * Audio Analysis Service
 * Handles audio file uploads and analysis using:
 * - Fine-tuned Gemini 1.5 Flash (Vertex AI) for audio analysis
 * - Standard Gemini 2.0 Flash (API) for text search (unchanged)
 */

import { API_CONFIG, AI_CONFIG } from '../config';
import { createFindAudioAnalysisPrompt } from '../prompts/findAudioAnalysisPrompt';
import { createAddTrackAnalysisPrompt } from '../prompts/addTrackAnalysisPrompt';
import { createFindAudioAnalysisPromptSimple } from '../prompts/findAudioAnalysisPromptSimple';
import { createAddTrackAnalysisPromptSimple } from '../prompts/addTrackAnalysisPromptSimple';

export interface AudioAnalysisResponse {
  generatedQuery: string;
  context: 'find' | 'add';
}

class AudioAnalysisService {
  // Vertex AI Configuration for Fine-tuned Model
  private readonly VERTEX_PROJECT_ID = 'audio-vault-dev';
  private readonly VERTEX_LOCATION = 'us-central1';
  private readonly FINE_TUNED_MODEL = 'projects/461875512024/locations/us-central1/models/832377581226426368';
  
  // Standard Gemini API Configuration (for fallback)
  private readonly GEMINI_API_BASE_URL = API_CONFIG.GEMINI.ENDPOINT;
  private readonly MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB limit for Vertex AI
  private readonly SUPPORTED_FORMATS = ['audio/mpeg', 'audio/wav', 'audio/mp4'];

  /**
   * Convert File object to base64 string
   */
  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;
        // Remove data URL prefix (data:audio/mpeg;base64,)
        const base64Data = base64String.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  /**
   * Validate audio file before processing
   */
  private validateAudioFile(file: File): void {
    if (!file) {
      throw new Error('No file provided');
    }

    if (file.size > this.MAX_FILE_SIZE) {
      throw new Error(`File size (${(file.size / (1024 * 1024)).toFixed(2)}MB) exceeds maximum limit (100MB for fine-tuned model)`);
    }

    if (!this.SUPPORTED_FORMATS.includes(file.type)) {
      throw new Error(`Unsupported file format: ${file.type}. Supported formats: ${this.SUPPORTED_FORMATS.join(', ')}`);
    }
  }

  /**
   * Create fine-tuned model prompt for audio analysis
   * Uses the same prompt pattern as training data
   */
  private createFineTunedAudioPrompt(): string {
    return `Analyze this audio track and provide a natural language description that captures the track's characteristics for metadata categorization. The description should include genre, mood, instruments, and atmosphere following this pattern:

"[mood] [time period] [genre] track with [instruments], [additional characteristics] atmosphere"

Focus on:
- One primary genre from the available collection
- Prominent mood and characteristics
- Only hero instruments (main/prominent instruments, not background elements)
- Descriptive atmosphere that aids in search and categorization

Provide a concise, natural description suitable for music library search.`;
  }

  /**
   * Create fallback prompt for standard Gemini API
   */
  private createFallbackAudioPrompt(context: 'find' | 'add', collectionData?: { 
    genres: { displayName: string }[], 
    tags: { displayName: string }[],
    categorizedTags?: {
      moods: string[],
      temporal: string[],
      instruments: string[],
      energy: string[],
      other: string[]
    }
  }, useSimplePrompt: boolean = false): string {
    if (useSimplePrompt) {
      return context === 'find' 
        ? createFindAudioAnalysisPromptSimple()
        : createAddTrackAnalysisPromptSimple();
    } else {
      return context === 'find' 
        ? createFindAudioAnalysisPrompt(collectionData)
        : createAddTrackAnalysisPrompt(collectionData);
    }
  }

  /**
   * Analyze audio file using fine-tuned Vertex AI model
   * Falls back to standard Gemini API if needed
   */
  async analyzeAudioFile(
    file: File,
    apiKey: string,
    context: 'find' | 'add' = 'add',
    collectionData?: { 
      genres: { displayName: string }[], 
      tags: { displayName: string }[],
      categorizedTags?: {
        moods: string[],
        temporal: string[],
        instruments: string[],
        energy: string[],
        other: string[]
      }
    },
    useSimplePrompt: boolean = false
  ): Promise<AudioAnalysisResponse> {
    // Validate file
    this.validateAudioFile(file);

    try {
      // Try fine-tuned model first (via backend endpoint)
      
      const result = await this.analyzeWithFineTunedModel(file, context, collectionData, useSimplePrompt);
      return result;
    } catch (error) {
      console.error('❌ Fine-tuned model FAILED - No fallback, throwing error:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        error
      });
      
      // Re-throw the error instead of falling back
      throw error;
    }
  }

  /**
   * Analyze audio using fine-tuned Vertex AI model
   */
  private async analyzeWithFineTunedModel(
    file: File,
    context: 'find' | 'add',
    collectionData?: { 
      genres: { displayName: string }[], 
      tags: { displayName: string }[],
      categorizedTags?: {
        moods: string[],
        temporal: string[],
        instruments: string[],
        energy: string[],
        other: string[]
      }
    },
    useSimplePrompt: boolean = false
  ): Promise<AudioAnalysisResponse> {
    
    // Go directly to Cloud Storage method (FormData always fails)
    return await this.tryBase64Upload(file, context, collectionData, useSimplePrompt);
  }

  private async tryFormDataUpload(
    file: File,
    context: 'find' | 'add'
  ): Promise<AudioAnalysisResponse> {
    
    // Convert file to FormData for backend upload
    const formData = new FormData();
    formData.append('audioFile', file);
    formData.append('context', context);
    formData.append('modelType', 'fine-tuned');
    

    // Call Firebase Function that handles Vertex AI fine-tuned model
    const response = await fetch('https://us-central1-audio-vault-dev.cloudfunctions.net/analyzeAudio', {
      method: 'POST',
      body: formData
    });

    return await this.processResponse(response);
  }

  private async tryBase64Upload(
    file: File,
    context: 'find' | 'add',
    collectionData?: { 
      genres: { displayName: string }[], 
      tags: { displayName: string }[],
      categorizedTags?: {
        moods: string[],
        temporal: string[],
        instruments: string[],
        energy: string[],
        other: string[]
      }
    },
    useSimplePrompt: boolean = false
  ): Promise<AudioAnalysisResponse> {
    // const startTime = performance.now();
    
    try {
      // Generate appropriate prompt based on context
      // const promptStart = performance.now();
      const customPrompt = useSimplePrompt 
        ? (context === 'find' 
            ? createFindAudioAnalysisPromptSimple()
            : createAddTrackAnalysisPromptSimple())
        : (context === 'find' 
            ? createFindAudioAnalysisPrompt(collectionData)
            : createAddTrackAnalysisPrompt(collectionData));
      // const promptTime = performance.now() - promptStart;
      
      
      // Upload file to Cloud Storage first instead of sending large base64
      const tempFileName = `temp-audio-analysis/${Date.now()}-${file.name}`;
      
      // We need to import Firebase storage here for direct upload
      // This is a simpler approach than the hook for this service
      // const firebaseImportStart = performance.now();
      const { initializeApp, getApps } = await import('firebase/app');
      const { getStorage, ref, uploadBytes } = await import('firebase/storage');
      // const firebaseImportTime = performance.now() - firebaseImportStart;
      
      // Initialize Firebase if not already done
      // const initStart = performance.now();
      const firebaseConfig = {
        projectId: 'audio-vault-dev',
        storageBucket: 'audio-vault-dev.appspot.com'
      };
      
      const app = getApps().length > 0 ? getApps()[0] : initializeApp(firebaseConfig);
      const storage = getStorage(app);
      const storageRef = ref(storage, tempFileName);
      // const initTime = performance.now() - initStart;
      
      // const uploadStart = performance.now();
      const uploadResult = await uploadBytes(storageRef, file, {
        contentType: file.type,
        customMetadata: {
          originalName: file.name,
          uploadedAt: new Date().toISOString(),
          purpose: 'audio-analysis-temp'
        }
      });
      // const uploadTime = performance.now() - uploadStart;
      
      
      // Get the gs:// URI (which the Firebase Function can access)
      const gsUri = `gs://${uploadResult.ref.bucket}/${uploadResult.ref.fullPath}`;
      
      // Send only the storage path to Firebase Function
      const requestBody = {
        storageUri: gsUri,
        fileName: file.name,
        mimeType: file.type,
        fileSize: file.size,
        context,
        modelType: 'fine-tuned',
        uploadMethod: 'storage-uri',
        customPrompt: customPrompt  // Send the appropriate prompt
      };
      

      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 30000); // 30 second timeout

      // const functionCallStart = performance.now();
      const response = await fetch('https://us-central1-audio-vault-dev.cloudfunctions.net/analyzeAudio', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });
      // const functionCallTime = performance.now() - functionCallStart;

      clearTimeout(timeoutId);

      // const responseProcessStart = performance.now();
      const result = await this.processResponse(response);
      // const responseProcessTime = performance.now() - responseProcessStart;
      
      // const totalTime = performance.now() - startTime;
      
      // Clean up the temporary file asynchronously (don't wait for it)
      this.cleanupFileAsync(storageRef).catch(error => {
        console.warn('⚠️ Async cleanup failed (non-blocking):', error);
      });
      
      return result;
      
    } catch (storageError) {
      console.error('❌ Cloud Storage upload failed:', storageError);
      throw new Error(`Cloud Storage upload failed: ${storageError instanceof Error ? storageError.message : 'Unknown error'}`);
    }
  }

  private async processResponse(response: Response): Promise<AudioAnalysisResponse> {

    if (!response.ok) {
      console.error('❌ Firebase Function returned error status:', response.status);
      let errorData;
      try {
        errorData = await response.json();
        console.error('📄 Error response body:', errorData);
      } catch (parseError) {
        console.error('❌ Could not parse error response as JSON');
        const textResponse = await response.text();
        console.error('📄 Raw error response:', textResponse);
        throw new Error(`Fine-tuned model error: HTTP ${response.status} - ${textResponse}`);
      }
      throw new Error(`Fine-tuned model error: ${errorData.error || 'Unknown error'}`);
    }

    const data = await response.json();
    
    if (data.success && data.description) {
      
      return {
        generatedQuery: data.description,
        context: data.context || 'add'
      };
    } else {
      console.error('❌ Unexpected response format from Firebase Function:', data);
      throw new Error('Invalid response format from fine-tuned model');
    }
  }

  /**
   * Fallback: Analyze audio using standard Gemini API
   */
  private async analyzeWithStandardGemini(
    file: File,
    apiKey: string,
    context: 'find' | 'add',
    collectionData?: { 
      genres: { displayName: string }[], 
      tags: { displayName: string }[],
      categorizedTags?: {
        moods: string[],
        temporal: string[],
        instruments: string[],
        energy: string[],
        other: string[]
      }
    },
    useSimplePrompt: boolean = false
  ): Promise<AudioAnalysisResponse> {
    // Convert to base64
    const audioBase64 = await this.fileToBase64(file);

    // Create the fallback prompt
    const prompt = this.createFallbackAudioPrompt(context, collectionData, useSimplePrompt);

    // Create request body for multimodal input
    const requestBody = {
      contents: [
        {
          role: "user",
          parts: [
            { text: prompt },
            {
              inlineData: {
                mimeType: file.type,
                data: audioBase64
              }
            }
          ]
        }
      ],
      generationConfig: AI_CONFIG.GEMINI.GENERATION_CONFIG
    };

    const response = await fetch(`${this.GEMINI_API_BASE_URL}?key=${apiKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Standard Gemini API Error:', errorData);
      throw new Error(`API error: ${errorData.error?.message || 'Unknown error'}`);
    }

    const data = await response.json();
    const textResponse = data.candidates?.[0]?.content?.parts?.[0]?.text || '';

    if (!textResponse) {
      throw new Error('No response text received from Gemini API');
    }

    // Clean up the response
    let generatedQuery = textResponse.trim();
    if (generatedQuery.startsWith('"') && generatedQuery.endsWith('"')) {
      generatedQuery = generatedQuery.slice(1, -1);
    }


    return {
      generatedQuery,
      context
    };
  }


  /**
   * Get supported file formats for UI display
   */
  getSupportedFormats(): string[] {
    return this.SUPPORTED_FORMATS;
  }

  /**
   * Get maximum file size for UI display
   */
  getMaxFileSize(): number {
    return this.MAX_FILE_SIZE;
  }

  /**
   * Cleanup storage file asynchronously (non-blocking)
   */
  private async cleanupFileAsync(storageRef: any): Promise<void> {
    // const cleanupStart = performance.now();
    try {
      const { deleteObject } = await import('firebase/storage');
      await deleteObject(storageRef);
      // const cleanupTime = performance.now() - cleanupStart;
    } catch (cleanupError) {
      // const cleanupTime = performance.now() - cleanupStart;
      console.warn('⚠️ Async cleanup failed:', cleanupError);
    }
  }

  /**
   * Get fine-tuned model information for debugging
   */
  getModelInfo(): { model: string, type: string, expires: string } {
    return {
      model: this.FINE_TUNED_MODEL,
      type: 'Gemini 1.5 Flash Fine-tuned',
      expires: 'September 24, 2025'
    };
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

export const audioAnalysisService = new AudioAnalysisService();